import React, { Children, FC, ReactElement, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { A11y, Navigation, SwiperOptions, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SliderRight } from './icon/icons/SliderRight';
import { SliderLeft } from './icon/icons/SliderLeft';
import { Typography } from './Typography';
import { Tag } from './Tag';
import { Kfh_PropertyTypeInterface } from '../types/graphqlTypes';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import { Photos } from './icon/icons/Photos';
import { Play } from './icon/icons/Play';

interface GalleryProps {
  property?: Kfh_PropertyTypeInterface;
  propertyCard?: boolean;
  onClick?: any;
  vertical?: boolean;
  disableGallerySwipeOnMobile?: boolean;
}

interface StyledGalleryProps {
  propertyCard?: boolean;
  vertical?: boolean;
  isClickable?: boolean;
}

interface StyledMainProps {
  vertical?: boolean;
  disableGallerySwipeOnMobile?: boolean;
}

// Styles

const StyledGallery = styled.div<StyledGalleryProps>`
  ${({ theme: { space, media, colors }, propertyCard, vertical, isClickable }) => css`
    position: relative;
    overflow: hidden;
    cursor: ${isClickable ? 'pointer' : 'default'};

    img {
      width: 100%; // poss temp whilst using test images
    }
    .gatsby-image-wrapper {
      background-color: ${colors.grey1.default};
      max-width: 100%; // cls fix
      height: auto; // cls fix
    }

    ${!vertical &&
    css`
      @media (min-width: ${media.xl}) {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        grid-gap: ${propertyCard ? space.xxs : space.lg};
      }
    `};

    ${propertyCard &&
    css`
      .gatsby-image-wrapper {
        border-radius: 0;
      }
    `};
  `}
`;

const StyledNavigation = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    --swiper-navigation-size: 32px;
    opacity: 0;

    .swiper-button-next,
    .swiper-button-prev {
      background: none;
      width: var(--swiper-navigation-size);
      height: var(--swiper-navigation-size);
      display: flex;
      justify-content: center;
      align-items: center;
      background-position: center center;
      background-repeat: no-repeat;
      padding: 0;
      top: 50%;
      bottom: 0;
      margin-top: 0;
      transform: translateY(-50%);

      &:hover {
        svg rect {
          opacity: 1;
        }
      }

      &:after {
        display: none;
        content: '';
      }
    }

    .swiper-button-next {
      right: ${space.md};
    }

    .swiper-button-prev {
      left: ${space.md};
    }

    // Tablet < styles

    @media (max-width: ${maxMedia.md}) {
      display: none;
    }
  `};
`;

const StyledOverlay = styled.span`
  ${({ theme: { space, global, layers } }) => css`
    position: absolute;
    left: ${space.md};
    top: ${space.md};
    display: flex;
    align-items: center;
    gap: ${space.md};
    border-radius: ${global.borderRadius.default};
    padding: ${space.sm} ${space.md};
    background: rgb(255 255 255 / 64%);
    z-index: ${layers.galleryOverlay};

    > div {
      display: flex;
      align-items: center;
      gap: ${space.sm};
    }
  `};
`;

const StyledPagination = styled.div<StyledGalleryProps>`
  ${({ theme: { space, colors, media, maxMedia } }) => css`
    --swiper-pagination-bullet-inactive-color: ${colors.white.default};
    --swiper-pagination-bullet-inactive-opacity: 0.64;
    bottom: ${space.sm}!important; // Swiper override
    height: ${space.md};

    // Tablet > styles

    @media (min-width: ${media.xl}) {
      display: none;
    }
  `};
`;

const StyledSecondaryImage = styled.div`
  ${({ theme: { maxMedia } }) => css`
    position: relative;

    img {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      object-fit: cover;
      transform: none;
    }

    // Tablet < styles

    @media (max-width: ${maxMedia.xl}) {
      display: none;
    }
  `};
`;

const StyledSwiperMain = styled.div<StyledMainProps>`
  ${({ theme: { space, colors, media, maxMedia }, vertical, disableGallerySwipeOnMobile }) => css`
    --swiper-theme-color: ${colors.brand.default};
    --swiper-pagination-color: #fff;
    position: relative;
    overflow: hidden;
    grid-column-start: 1;
    grid-column-end: 3;
    grid-row-start: 1;
    grid-row-end: 3;

    @media (max-width: ${maxMedia.md}) {
      ${disableGallerySwipeOnMobile &&
      css`
        .swiper-pagination-bullets-dynamic {
          display: none;
        }
      `};
    }

    .swiper {
      height: 100%;
    }

    .swiper-button-lock {
      display: none !important;
    }

    .swiper-container {
      width: 100vw;
      height: 100%;
      padding-bottom: ${space.xl};
    }

    .swiper-slide {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      flex-grow: 0;
      flex-shrink: 0;
      width: auto;
      height: auto;

      > * {
        width: 100%;
      }
    }

    &:hover {
      ${StyledNavigation} {
        opacity: 1;
      }
    }

    ${!vertical &&
    css`
      @media (min-width: ${media.xl}) {
        .swiper-slide {
          .gatsby-image-wrapper {
            height: 100%;
          }

          > * {
            height: 100%;
          }
        }
      }
    `};
  `};
`;

const StyledTags = styled.ul`
  ${({ theme: { space, media, maxMedia, layers } }) => css`
    position: absolute;
    z-index: ${layers.galleryOverlay};

    li {
      display: flex;

      & + li {
        margin-top: ${space.xxs};
      }
    }

    // Mobile styles

    @media (max-width: ${maxMedia.xl}) {
      top: ${space.md};
      right: ${space.md};

      li {
        justify-content: flex-end;
      }
    }

    // Desktop styles

    @media (min-width: ${media.xl}) {
      bottom: ${space.md};
      left: ${space.md};
    }
  `};
`;

const StyledOpenDayBanner = styled(Typography)`
  ${({ theme: { space, colors, media, maxMedia, global, layers } }) => css`
    padding: ${space.sm} ${space.md};
    background-color: ${colors.accent1.default};
    color: ${colors.accent1.contrast};

    @media (max-width: ${maxMedia.xl}) {
      width: 100%;
    }

    @media (min-width: ${media.xl}) {
      position: absolute;
      right: calc(352px + ${space.lg} + ${space.md});
      top: ${space.md};
      z-index: ${layers.galleryOverlay};
      border-radius: ${global.borderRadius.default};
    }
  `};
`;

export const Gallery: FC<React.PropsWithChildren<GalleryProps>> = ({
  children,
  property,
  propertyCard,
  onClick,
  vertical,
  disableGallerySwipeOnMobile,
}) => {
  const [activeImage, setActiveImage] = useState<number>(0);
  const [controlledSwiper, setControlledSwiper] = useState<any>(null);
  const hasMount = useRef(false);
  const prevNav = useRef<HTMLButtonElement>(null);
  const nextNav = useRef<HTMLButtonElement>(null);
  const pagination = useRef<HTMLDivElement>(null);
  const { dataLayerPush } = useDataLayerPush();

  useEffect(() => {
    if (controlledSwiper && !hasMount.current) {
      controlledSwiper.init();
      hasMount.current = true;

      setTimeout(() => {
        controlledSwiper.update();
      }, 300);
    }
  }, [controlledSwiper]);

  const swiperDefaultRules = {
    preloadImages: true,
    updateOnImagesReady: true,
    loop: false,
    slidesPerView: 1,
    watchOverflow: true,
    watchSlidesProgress: true,
    preventInteractionOnTransition: true,
    navigation: false,
    pagination: {
      el: pagination.current,
      type: 'bullets',
      dynamicBullets: true,
      clickable: false,
    },
    a11y: {
      enabled: true,
      slideRole: 'listitem',
    },
  };

  const swiperSwipeRules = !disableGallerySwipeOnMobile
    ? {
        ...swiperDefaultRules,
      }
    : {
        ...swiperDefaultRules,
        allowSlideNext: false,
        allowTouchMove: false,
        breakpoints: {
          576: {
            allowSlideNext: true,
            allowTouchMove: true,
          },
        },
      };

  const mainOpts: SwiperOptions = swiperSwipeRules;

  const dirtyCount = Children.count(children);
  if (!children || dirtyCount <= 0) {
    return null;
  }

  // Clean children array - check for nulls and undefined

  const cleansedChildren =
    dirtyCount === 1 ? Children.toArray(children) : Children.toArray(children).filter((el) => el != null);

  const cleanCount = Children.count(cleansedChildren);

  const handleClick = (...props) => {
    //   dataLayerPush({
    //     event: variation === 'arrowLink' ? 'Click arrow link' : 'Click CTA button',
    //     clickText: label,
    //     clickURL: externalUrl || to,
    //     clickGroup: dataLayerClickGroup,
    //   });

    if (onClick) {
      onClick(...props);
    }
  };

  // Clean Day and Time string (for open day)

  const cleansedDateTime = (dateTime: string) => dateTime.replace('T', ' (').replace(':00Z', ')');

  return (
    <StyledGallery propertyCard={propertyCard} vertical={vertical} isClickable={!!onClick}>
      {/* Property open day */}

      {property && property.openDay ? (
        <StyledOpenDayBanner component="p" variant="bodySmall">
          <strong>Open day</strong>{' '}
          {property.openDayDateStart && property.openDayDateEnd ? (
            <>
              {cleansedDateTime(property.openDayDateStart)} - {cleansedDateTime(property.openDayDateEnd)}
            </>
          ) : (
            ''
          )}
        </StyledOpenDayBanner>
      ) : (
        ''
      )}

      <StyledSwiperMain vertical={vertical} disableGallerySwipeOnMobile={disableGallerySwipeOnMobile}>
        {/* Media count overlay */}

        <StyledOverlay>
          <div>
            <Photos />
            <Typography component="span" variant="titleExtraSmall">
              {activeImage + 1} / {Children.count(cleansedChildren)}
            </Typography>
          </div>

          {/* Virtual viewing */}

          {property && property.virtualViewings && propertyCard ? (
            <div>
              <Play />
              <Typography component="span" variant="titleExtraSmall">
                Tour
              </Typography>
            </div>
          ) : (
            ''
          )}
        </StyledOverlay>
        {/* // Main gallery images */}

        <Swiper
          {...mainOpts}
          onSwiper={setControlledSwiper}
          wrapperTag="ul"
          modules={[Navigation, A11y, Pagination]}
          onSlideChange={(swiper) => {
            setActiveImage(swiper.realIndex);
          }}
          onTouchEnd={(swiper) => {
            const isLast = swiper.activeIndex === swiper.slides.length - 1;
            const isAtEnd = swiper.isEnd;

            if (isLast && isAtEnd && swiper.touches.diff < -50) {
              swiper.slideTo(0);
            }
          }}
          onClick={handleClick}
          className="gallery-swiper"
        >
          {cleanCount >= 1 &&
            React.Children.map(cleansedChildren, (slide, i) => {
              if (React.isValidElement(slide)) {
                return (
                  <SwiperSlide key={`swiper-${i}`} tag="li" role="listitem">
                    {slide}
                  </SwiperSlide>
                );
              }
              return null;
            })}
        </Swiper>

        {/* Swiper pagination */}

        <StyledPagination className="swiper-pagination" ref={pagination} />

        {/* Swiper navigation controls */}

        <StyledNavigation>
          <button
            type="button"
            className="swiper-button-prev"
            aria-label="Previous"
            onClick={() => {
              dataLayerPush({
                event: 'gallery scroll',
                clickText: 'previous',
              });
              if (controlledSwiper) {
                const newIndex = activeImage === 0 ? cleanCount - 1 : activeImage - 1;
                controlledSwiper.slideTo(newIndex);
              }
            }}
          >
            <SliderLeft size="default" />
          </button>
          <button
            type="button"
            className="swiper-button-next"
            aria-label={activeImage + 1 === cleanCount ? 'Go to first image' : 'Next image'}
            onClick={() => {
              dataLayerPush({
                event: 'gallery scroll',
                clickText: activeImage + 1 === cleanCount ? 'restart' : 'next',
              });

              if (controlledSwiper) {
                const nextIndex = activeImage + 1 === cleanCount ? 0 : activeImage + 1;
                controlledSwiper.slideTo(nextIndex);
              }
            }}
          >
            <SliderRight size="default" />
          </button>
        </StyledNavigation>

        {/* Property tags and banners */}

        {property && (
          <StyledTags>
            {property.forSaleAndToLet && (
              <li>
                <Tag text="For sale or to let" backgroundColor="accent2" />
              </li>
            )}
            {property.riverside && (
              <li>
                <Tag text="Riverside" backgroundColor="accent2" />
              </li>
            )}
            {property.managed && (
              <li>
                <Tag text="Managed by KFH" backgroundColor="accent2" />
              </li>
            )}
            {property.zeroDeposit && (
              <li>
                <Tag
                  text="Zero Deposit Available"
                  backgroundColor="accent2"
                  tooltipText="Pay less upfront with a Zero Deposit Guarantee in place of a traditional tenancy deposit."
                />
              </li>
            )}
            {property.propertyAge === 'New home' && (
              <li>
                <Tag text="New home" backgroundColor="accent2" />
              </li>
            )}
            {property?.__typename === 'KFH_DevelopmentResalePropertyType' && (
              <li>
                <Tag text="New Development" backgroundColor="accent2" />
              </li>
            )}
            {property.helpToBuy && (
              <li>
                <Tag text="Help to Buy" backgroundColor="accent2" />
              </li>
            )}
            {property.availableLongLet && (
              <li>
                <Tag text="Long let" backgroundColor="accent2" />
              </li>
            )}
            {property.availableShortLet && (
              <li>
                <Tag text="Short let" backgroundColor="accent2" />
              </li>
            )}
            {property.sharedOwnership && (
              <li>
                <Tag text="Shared Ownership" backgroundColor="accent2" />
              </li>
            )}
          </StyledTags>
        )}
      </StyledSwiperMain>

      {!vertical && (
        <>
          {cleanCount === 1 ? (
            <>
              <StyledSecondaryImage>{cleansedChildren[0]}</StyledSecondaryImage>
              <StyledSecondaryImage>{cleansedChildren[0]}</StyledSecondaryImage>
            </>
          ) : (
            <>
              {/* Secondary (top right) Image */}

              <StyledSecondaryImage>
                {cleanCount === activeImage + 1
                  ? cleansedChildren[0]
                  : (cleansedChildren[activeImage + 1] as ReactElement)}
              </StyledSecondaryImage>

              {/* Secondary (bottom right) Image OR CTA button */}

              <StyledSecondaryImage>
                {cleanCount === activeImage + 1 && (cleansedChildren[1] as ReactElement)}
                {cleanCount - 1 === activeImage + 1 && (cleansedChildren[0] as ReactElement)}
                {cleanCount - 1 !== activeImage + 1 && cleanCount !== activeImage + 1
                  ? (cleansedChildren[activeImage + 2] as ReactElement)
                  : ''}
              </StyledSecondaryImage>
            </>
          )}
        </>
      )}
    </StyledGallery>
  );
};
